import { useIsMutating } from '@tanstack/react-query';
import { Avatar } from 'primereact/avatar';
import { AvatarGroup } from 'primereact/avatargroup';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useRef } from 'react';

import {
    CONDITION_WATCHER_MUTATION_KEY,
    useAddConditionWatcherMutation,
    useRemoveConditionWatcherMutation,
} from '@/components/shared/api/mutations/conditions';

import {
    NoWatchersText,
    WatcherList,
    WatcherPopoverBody,
    WatcherPopoverTitle,
    WatcherRow,
    WatcherRowAction,
    WatcherRowAvatarWrapper,
    WatcherRowDate,
    WatcherRowMeta,
    WatcherRowName,
    WatcherStackButton,
    WatcherStackLoadingOverlay,
    WatchThisButton,
} from './ApplicantConditions.StyledComponents';
import { ConditionWatcher } from './ApplicantConditions.types';
import { formatDateShort, getInitials } from './ApplicantConditions.utils';

const MAX_VISIBLE_AVATARS = 5;

interface WatcherAvatarStackProps {
    applicantId: string;
    conditionId: string;
    watchers: ConditionWatcher[];
    isWatching?: boolean;
}

export const WatcherAvatarStack = ({
    applicantId,
    conditionId,
    watchers,
    isWatching,
}: WatcherAvatarStackProps) => {
    const overlayRef = useRef<OverlayPanel>(null);
    const addWatcherMutation = useAddConditionWatcherMutation();
    const removeWatcherMutation = useRemoveConditionWatcherMutation();

    const pendingWatcherMutations = useIsMutating({
        mutationKey: CONDITION_WATCHER_MUTATION_KEY,
        predicate: (mutation) => {
            const variables = mutation.state.variables as { conditionId?: string } | undefined;
            return variables?.conditionId === conditionId;
        },
    });
    const isWatcherLoading = pendingWatcherMutations > 0;

    const visibleWatchers = watchers.slice(0, MAX_VISIBLE_AVATARS);
    const overflowCount = watchers.length - visibleWatchers.length;
    const watcherCountLabel = `${watchers.length} watcher${watchers.length === 1 ? '' : 's'}`;

    const handleToggle = (e: React.MouseEvent) => {
        overlayRef.current?.toggle(e);
    };

    const handleToggleWatchClick = () => {
        const request = { applicantId, conditionId };
        if (isWatching) {
            removeWatcherMutation.mutate(request);
        } else {
            addWatcherMutation.mutate(request);
        }
    };

    return (
        <>
            <WatcherStackButton
                type="button"
                onClick={handleToggle}
                aria-busy={isWatcherLoading || undefined}
                aria-label={
                    isWatcherLoading
                        ? 'Updating watchers…'
                        : watchers.length > 0
                          ? watcherCountLabel
                          : 'No watchers yet — click to watch'
                }
                data-testid={`condition-watchers-${conditionId}`}
            >
                {watchers.length > 0 ? (
                    <AvatarGroup>
                        {visibleWatchers.map((watcher) => (
                            <Avatar
                                key={watcher.orpUserProfileId}
                                image={watcher.avatarUrl ?? undefined}
                                label={
                                    watcher.avatarUrl
                                        ? undefined
                                        : getInitials(watcher.displayName || watcher.email)
                                }
                                imageAlt={watcher.displayName ?? watcher.email}
                                shape="circle"
                            />
                        ))}
                        {overflowCount > 0 ? (
                            <Avatar label={`+${overflowCount}`} shape="circle" />
                        ) : null}
                    </AvatarGroup>
                ) : (
                    <Avatar icon="pi pi-eye" shape="circle" />
                )}
                {isWatcherLoading ? (
                    <WatcherStackLoadingOverlay
                        aria-hidden
                        data-testid={`condition-watchers-loading-${conditionId}`}
                    >
                        <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="4" />
                    </WatcherStackLoadingOverlay>
                ) : null}
            </WatcherStackButton>
            <OverlayPanel
                ref={overlayRef}
                data-testid={`condition-watchers-popover-${conditionId}`}
            >
                <WatcherPopoverBody>
                    <WatcherPopoverTitle>{watcherCountLabel}</WatcherPopoverTitle>
                    {watchers.length === 0 ? (
                        <NoWatchersText>No one is watching yet.</NoWatchersText>
                    ) : (
                        <WatcherList>
                            {watchers.map((watcher) => (
                                <WatcherRow key={watcher.orpUserProfileId}>
                                    <WatcherRowAvatarWrapper>
                                        <Avatar
                                            image={watcher.avatarUrl ?? undefined}
                                            label={
                                                watcher.avatarUrl
                                                    ? undefined
                                                    : getInitials(
                                                          watcher.displayName || watcher.email,
                                                      )
                                            }
                                            imageAlt={watcher.displayName ?? watcher.email}
                                            shape="circle"
                                        />
                                    </WatcherRowAvatarWrapper>
                                    <WatcherRowMeta>
                                        <WatcherRowName>
                                            {watcher.displayName ?? watcher.email}
                                        </WatcherRowName>
                                        <WatcherRowDate>
                                            Watching since {formatDateShort(watcher.dateCreated)}
                                        </WatcherRowDate>
                                    </WatcherRowMeta>
                                    <WatcherRowAction aria-hidden />
                                </WatcherRow>
                            ))}
                        </WatcherList>
                    )}
                    <WatchThisButton
                        type="button"
                        onClick={handleToggleWatchClick}
                        disabled={isWatcherLoading}
                        data-testid={`condition-watch-${conditionId}`}
                    >
                        {isWatching
                            ? 'Stop watching this task/condition'
                            : '👀 Watch this task/condition'}
                    </WatchThisButton>
                </WatcherPopoverBody>
            </OverlayPanel>
        </>
    );
};
